/**
 * A string templating system. Parses strings with double-braced
 * placeholders and creates instances which can later be filled by
 * providing values for said placeholders.
 *
 * This is intended for end-users, so it provides a rich set of error,
 * warning and hint diagnostics.
 *
 * @module
 */
import { type IDynamicProperty } from 'stores/DynamicProperties/IDynamicProperty';
import type Asset from 'stores/DynamicProperties/Types/Asset';
import type BooleanProperty from 'stores/DynamicProperties/Types/Boolean';
import type Color from 'stores/DynamicProperties/Types/Color';
import type Dimensions from 'stores/DynamicProperties/Types/Dimensions';
import type DynamicGroup from 'stores/DynamicProperties/Types/Group';
import type NumberProperty from 'stores/DynamicProperties/Types/Number';
import type Percentage from 'stores/DynamicProperties/Types/Percentage';
import type Pixels from 'stores/DynamicProperties/Types/Pixels';
import type Template from 'stores/DynamicProperties/Types/Template';
import type Text from 'stores/DynamicProperties/Types/Text';
import { DynamicPropertyType } from 'types/DynamicPropertyType';

// MOSTLY THE SAME AS functions/src/build/labeltemplate.ts
// There are dependency differences owing to each codebase's different take
// on managing Dynamic Properties. Like many things it would be nice to
// merge them one day, but the backend labeling has been stable for a while
// so hopefully there shouldn't be much churn.

// Unique helper utils due to different interface with the rest of the app

function isAnyDynamicProperty(x: unknown): x is IDynamicProperty<unknown> {
    return (
        x != null &&
        typeof x === 'object' &&
        'name' in x &&
        typeof x.name === 'string' &&
        'type' in x &&
        typeof x.type === 'string' &&
        'value' in x &&
        // Behavior, not data: a method is what sets a live property apart
        // from its serialized { name, type, value } form, and every live
        // property has getState.
        'getState' in x &&
        typeof x.getState === 'function'
    ); // ok that's good enough
}

// Note DIMENSIONS appears as both a mapping type and a scalar type. That's
// allowed because it has a canonical string representation
// `${width}x${height}`.
type MappingDynamicProperty = Dimensions | DynamicGroup;
const MAPPING_TYPES: ReadonlySet<string> = new Set([
    DynamicPropertyType.DIMENSIONS,
    DynamicPropertyType.GROUP,
]);

function isMappingProperty(
    x: IDynamicProperty<unknown>
): x is MappingDynamicProperty {
    return MAPPING_TYPES.has(x.type);
}

type ScalarDynamicProperty =
    | Asset
    | BooleanProperty
    | Color
    | Dimensions
    | NumberProperty
    | Percentage
    | Pixels
    | Template
    | Text;

const SCALAR_TYPES: ReadonlySet<string> = new Set([
    DynamicPropertyType.ASSET,
    DynamicPropertyType.BOOLEAN,
    DynamicPropertyType.COLOR,
    DynamicPropertyType.DIMENSIONS,
    DynamicPropertyType.NUMBER,
    DynamicPropertyType.PERCENTAGE,
    DynamicPropertyType.PIXELS,
    DynamicPropertyType.TEMPLATE,
    DynamicPropertyType.TEXT,
]);

function isScalarProperty(
    x: IDynamicProperty<unknown>
): x is ScalarDynamicProperty {
    return SCALAR_TYPES.has(x.type);
}

function getMappingProperty(dynprop: MappingDynamicProperty, key: string) {
    return dynprop.getChild(key);
}

function getPropertyLabelRepr(
    dynprop: ScalarDynamicProperty,
    path: ReadonlyArray<string>,
    outMessages: string[]
) {
    // The ladder is deliberately short. `getString()` used to sit between
    // these two rungs, but it can never rescue a falsy `label`: the base
    // `get label()` is `return this.getString()`, so for every type that
    // doesn't override `label` the two are the same expression. Of the two
    // that do override it, Template's `getString()` resolves to the same
    // `store.data.name`, and Asset's returns an absolute URL where `label`
    // returns the asset path - a downgrade inside a label, not a fallback.
    // It was also the only rung that could throw (Asset.getString reaches
    // Firebase Storage's ref() and `new URL()`, both of which reject bad
    // input).
    try {
        const label = dynprop.label;
        if (label) return label;

        // `value` is the last resort, and only when it is already a
        // primitive. Dimensions extends DynamicGroup, so its `value` is an
        // array of child properties; toLabelString would render that as
        // "[object Object],[object Object]". null/undefined is passed
        // through so toLabelString can raise its own bad-value warning,
        // matching what a plain object at this path would do.
        const value = dynprop.value;
        if (value == null || typeof value !== 'object') return value;

        outMessages.push(
            `Evaluating ${keyPathToString(path)}: ` +
                `${dynprop.type} property has no label representation`
        );
        return ERROR;
    } catch (propertyError) {
        outMessages.push(
            `Evaluating ${keyPathToString(path)}: ${propertyError}`
        );
        return ERROR;
    }
}

// Copied from adhublib#debugutils

function keyPathToString(keyPath: ReadonlyArray<string | number>): string {
    let pathString = '';
    let keyDelim = '';
    for (const k of keyPath) {
        if (typeof k === 'number') {
            pathString += '[' + k + ']';
        } else {
            pathString += keyDelim + k;
        }
        keyDelim = '.';
    }
    return pathString || '(object root)';
}

// Original labeltemplate.ts starts here

// Conceptually this is very simple - your standard {{ template }}
// substitution. It could be done in a handful of lines with a single
// regex. But with the extra code here we can provide detailed error
// messages and hints.

interface BaseRenderer {
    readonly text: string;
    readonly start: number;
    readonly end: number;
}

interface Literal extends BaseRenderer {
    readonly type: 'literal';
}

interface Field extends BaseRenderer {
    readonly type: 'field';
    readonly path: ReadonlyArray<string>;
}
type Renderer = Literal | Field;

type TokenType = 'text' | 'open' | 'close' | 'end';
interface Token {
    readonly type: TokenType;
    readonly text: string;
    readonly start: number;
    readonly end: number;
}

const ERROR = Symbol('utils/LabelTemplate#ERROR');

function messageUnexpectedToken(token: Token) {
    return `Unexpected token '${token.text}' at position ${token.start}`;
}

function messageUnclosedPlaceholder(token: Token) {
    return `Placeholder at position ${token.start} has no closing }}`;
}

function messageEmptyPlaceholder(token: Token) {
    return `Placeholder at position ${token.start} is empty`;
}

function messageEmptyPathPart(token: Token, path: string) {
    return (
        `Placeholder '${path}' at position ${token.start} is invalid.` +
        ' Placeholders cannot begin or end with a dot, or contain consecutive dots.'
    );
}

function messageBadBraceHint(
    sample: string,
    start: number,
    fieldName?: string
) {
    let message =
        `The text '${sample}' at position ${start} has no effect.` +
        ' Placeholders must begin with {{ and end with }}.';

    if (fieldName) {
        const dym = '{{' + fieldName + '}}';
        message += ` Did you mean ${dym}?`;
    }

    return message;
}

function messagePossibleBadValue(path: ReadonlyArray<string>, value: any) {
    return `'${keyPathToString(path)}' evaluated to ${value}`;
}

function messageLabelStringBrokeContract(path: ReadonlyArray<string>) {
    return `toLabelString method on '${keyPathToString(
        path
    )}' did not return a string`;
}

function messageUselessStringConversion(path: ReadonlyArray<string>) {
    return `Evaluation of '${keyPathToString(
        path
    )}' used default string conversion`;
}

// while the correct syntax involves {{ to open placeholders and }} to
// close them, we match any number of them so we can recover from parse
// errors and be more helpful when reporting them
const BRACES = /(\x7B+|\x7D+)/g;

const INF_LOOP = 100000;

function tokenize(templateString: string) {
    let i = 0;
    const tokens: Token[] = templateString.split(BRACES).map((text) => {
        const start = i;
        i += text.length;
        const end = i;
        let type: TokenType = 'text';
        if (text) {
            if (text[0] === '{') {
                type = 'open';
            } else if (text[0] === '}') {
                type = 'close';
            }
        }
        return { type, text, start, end };
    });
    tokens.push({ type: 'end', text: '', start: i, end: i });
    return tokens;
}

class StringTemplateParser {
    private readonly text: string;
    private readonly tokens: Token[];
    private readonly count: number;
    private pos = 0;

    messages: string[] = [];
    renderers: Renderer[] = [];

    constructor(templateString: string) {
        this.text = templateString;
        this.tokens = tokenize(this.text);
        this.count = this.tokens.length;
    }

    message(text: string) {
        this.messages.push(text);
    }

    pushAsLiterals(...tokens: Token[]) {
        for (const token of tokens) {
            if (token && token.type !== 'end' && token.text) {
                this.renderers.push({
                    type: 'literal',
                    text: token.text,
                    start: token.start,
                    end: token.end,
                });
            }
        }
    }

    parse() {
        for (let i = 0; i < INF_LOOP; i++) {
            if (this.pos < this.count) {
                this.parse1();
            } else {
                return this.renderers;
            }
        }
        throw new Error('Assert: runaway loop');
    }

    parse1() {
        const token = this.tokens[this.pos++];
        switch (token.type) {
            case 'open':
                this.parsePlaceholder(token);
                break;
            case 'end':
                break;
            default:
                this.pushAsLiterals(token);
                break;
        }
    }

    private parsePlaceholder(open: Token) {
        const openOk = open.text.length === 2;

        const fieldName = this.tokens[this.pos++];
        if (fieldName.type !== 'text') {
            switch (fieldName.type) {
                case 'end':
                case 'open':
                    if (openOk) this.message(messageUnclosedPlaceholder(open));
                    break;
                case 'close':
                    if (openOk) this.message(messageEmptyPlaceholder(open));
                    break;
            }
            this.pushAsLiterals(open);
            this.pos--;
            return;
        }

        const close = this.tokens[this.pos++];
        if (close.type !== 'close') {
            switch (close.type) {
                case 'end':
                case 'open':
                    if (openOk) this.message(messageUnclosedPlaceholder(open));
                    break;
                default:
                    this.message(messageUnexpectedToken(fieldName));
                    break;
            }
            this.pushAsLiterals(open);
            this.pos -= 2;
            return;
        }

        const closeOk = close.text.length === 2;
        const fieldPath = this.validateFieldPath(fieldName, openOk && closeOk);

        if (!openOk || !closeOk) {
            this.message(
                messageBadBraceHint(
                    this.text.slice(open.start, close.end),
                    open.start,
                    // only add a did-you-mean if the fieldPath is otherwise valid
                    fieldPath ? fieldName.text : undefined
                )
            );
        }

        if (!openOk || !closeOk || !fieldPath) {
            this.pushAsLiterals(open, fieldName, close);
            return;
        }

        this.renderers.push({
            type: 'field',
            text: open.text + fieldName.text + close.text,
            start: open.start,
            end: close.start,
            path: fieldPath,
        });
    }

    private validateFieldPath(fieldName: Token, warnEmpty: boolean) {
        const pathStr = fieldName.text.trim();
        if (!pathStr) {
            if (warnEmpty) this.message(messageEmptyPlaceholder(fieldName));
            return undefined;
        }

        const path = pathStr.split('.');
        if (path.some((part) => !part)) {
            this.message(messageEmptyPathPart(fieldName, pathStr));
            return undefined;
        }

        return path;
    }
}

const LABEL_TYPEOFS: ReadonlySet<string> = new Set([
    typeof true,
    typeof 1,
    typeof '',
    'bigint',
]);

function isLabelAppropriateValue(v: any): boolean {
    return LABEL_TYPEOFS.has(typeof v);
}

const BUILTIN_TOSTRING = new Set([
    Object.prototype.toString,
    Function.prototype.toString,
    Error.prototype.toString,
    RegExp.prototype.toString,
    Array.prototype.toString,
    Map.prototype.toString,
    Set.prototype.toString,
    WeakMap.prototype.toString,
    WeakSet.prototype.toString,
    Promise.prototype.toString,
]);

function hasDefaultToString(x: any): boolean {
    return !x || !('toString' in x) || BUILTIN_TOSTRING.has(x.toString);
}

function lookup(
    values: any,
    path: ReadonlyArray<string>,
    outMessages: string[]
): any {
    let node = values;

    for (let i = 0; i < path.length; i++) {
        const key = path[i];
        try {
            if (isAnyDynamicProperty(node)) {
                if (isMappingProperty(node)) {
                    node = getMappingProperty(node, key);
                } else {
                    outMessages.push(
                        `Evaluating ${keyPathToString(path)}: Property type ${node.type} is not a property group`
                    );
                    return ERROR;
                }
            } else if (node instanceof Map) {
                node = node.get(key);
            } else {
                node = node[key];
            }
        } catch (lookupError) {
            outMessages.push(
                `Evaluating ${keyPathToString(path)}: ${lookupError}`
            );
            return ERROR;
        }
    }

    if (isAnyDynamicProperty(node)) {
        if (isScalarProperty(node)) {
            return getPropertyLabelRepr(node, path, outMessages);
        }
        outMessages.push(
            `Evaluating ${keyPathToString(path)}: Property type ${node.type} not supported for labels`
        );
        return ERROR;
    }

    return node;
}

function toLabelString(
    value: any,
    path: ReadonlyArray<string>,
    outMessages: string[]
): string {
    if (value == null) {
        outMessages.push(messagePossibleBadValue(path, value));
        return `${value}`;
    }

    if (typeof value === 'number') {
        if (!isFinite(value)) {
            // alert about NaNs and infinites
            outMessages.push(messagePossibleBadValue(path, value));
        }
        return `${value}`;
    }

    if (isLabelAppropriateValue(value)) {
        return `${value}`;
    }

    if ('toISOString' in value) {
        return value.toISOString();
    }

    if ('toLabelString' in value) {
        const labelString = value.toLabelString();
        if (typeof labelString !== 'string') {
            outMessages.push(messageLabelStringBrokeContract(path));
        }
        return `${labelString}`;
    }

    if ('toString' in value) {
        if (hasDefaultToString(value)) {
            outMessages.push(messageUselessStringConversion(path));
        }
        return `${value.toString()}`;
    }

    return `${value}`;
}

/**
 * The result of calling {@link StringTemplate.fill}.
 */
export class StringTemplateFillResult {
    /**
     * Constructs a new `StringTemplateFillResult`. Used by
     * {@link StringTemplate.fill}.
     *
     * @param text - The resulting filled text.
     * @param messages - Any errors, warnings or hints that occurred while
     *   filling the text. These are user-readable strings. Example
     *   warnings include: use of null, undefined, or NaN values, dubiously
     *   useful string conversions like "[Object object]", etc.
     */
    constructor(
        public readonly text: string,
        public readonly messages: ReadonlyArray<string>
    ) {}

    /**
     * Returns the resulting filled text.
     */
    toString() {
        return this.text;
    }
}

/**
 * A string template with placeholders that can be later filled with
 * values.
 *
 * Placeholders take the form `{{fieldName}}`. That is, they start with two
 * open braces and end with two closing braces. Spaces may be placed
 * between braces and the field name for legibility - they are ignored.
 *
 * `fieldName` is a property or key that will be looked up on a value
 * object passed to the `fill` method. It may be a dot-separated series of
 * names to access nested properties. A name is allowed to be a
 * non-negative integer; this can be used to access array elements.
 *
 * Note that square brackets are not recognized. For example, use
 * `{{images.0.maskUrl}}`, not `{{images[0].maskUrl}}`
 *
 * There is currently no escape functionality. That is, there is no way to
 * include an intentional literal `{{` in the output text.
 */
export class StringTemplate {
    /**
     * Any errors, warnings or hints that occurred while parsing the
     * template. These are user-readable strings. Example warnings include:
     * mismatched braces, incorrect number of braces, invalid placeholder
     * names, etc.
     */
    public readonly messages: ReadonlyArray<string>;
    private readonly renderers: Renderer[];

    /**
     * Constructs a new `StringTemplate`.
     *
     * @param text - The template text, with placeholders.
     */
    constructor(public readonly text: string) {
        const parser = new StringTemplateParser(text);
        this.renderers = parser.parse();
        this.messages = parser.messages;
    }

    /**
     * Fills the template with the provided values.
     *
     * @param values - A plain object, `Array`, `Map`, or
     *   {@link dyndata/properties.MappingProperty} containing values for
     *   the placeholders. Nested objects for use with dot-separated field
     *   names may also be any of these four types. Leaf values may be
     *   anything that can be converted to a string. Objects are converted
     *   to strings by looking for methods named `toISOString`,
     *   `toLabelString`, and finally `toString` in that order. `null`,
     *   `undefined` and `NaN` are allowed but will add warnings to the
     *   result object. Attempts to look up nested names on non-existent
     *   objects will not throw an Error, but will also add warnings to the
     *   result object.
     * @returns A {@link StringTemplateFillResult} containing the filled
     *   text, plus any status messages.
     */
    fill(values: any): StringTemplateFillResult {
        const messages: string[] = [];
        const text = this.renderers
            .map((el) => {
                switch (el.type) {
                    case 'literal': {
                        return el.text;
                    }
                    case 'field': {
                        const value = lookup(values, el.path, messages);
                        if (value === ERROR) {
                            return el.text;
                        } else {
                            return toLabelString(value, el.path, messages);
                        }
                    }
                }
            })
            .join('');

        return new StringTemplateFillResult(text, messages);
    }
}

/**
 * Parses a string template and fills it in one call.
 *
 * @param template - The template string to parse.
 * @param values - The values to insert into placeholders.
 * @returns A {@link StringTemplateFillResult} containing the filled text,
 *   plus any status messages from both the parse and fill phases.
 */
export function fill(template: string, values: any): StringTemplateFillResult {
    const templateInstance = new StringTemplate(template);
    const { text, messages } = templateInstance.fill(values);
    return new StringTemplateFillResult(
        text,
        templateInstance.messages.concat(messages)
    );
}
